<template>
    <div class="row-i" :class="{'is-dominant': dominant, 'is-error': !!error}" :id="rowId">
        <div class="row-top">
            <div>
                <div class="row-name" :title="ingredient.pivot_label || ingredient.label">{{ ingredient.pivot_label || ingredient.label }}</div>
                <div v-if="ingredient.pivot_label" class="row-ref">{{ ingredient.label }}</div>
                <div>
                    <span v-if="ingredient.pivot_type_label" class="tag tag--on">{{ optionLabel('label_product', ingredient.pivot_type_label) }}</span>
                    <span v-if="ingredient.pivot_provenance" class="tag" :class="{'tag--warn': isImported}">{{ optionLabel('provenance', ingredient.pivot_provenance) }}</span>
                    <span v-if="ingredient.pivot_mode_conserv" class="tag">{{ optionLabel('mode_conserv', ingredient.pivot_mode_conserv) }}</span>
                    <span v-if="hasSaison && ingredient.pivot_saison" class="tag" :class="ingredient.pivot_saison === 'de_saison' ? 'tag--on' : 'tag--warn'">{{ ingredient.pivot_saison === 'de_saison' ? 'De saison' : 'Hors saison' }}</span>
                    <span v-if="ingredient.espece_menace" class="tag tag--warn">Espèce menacée</span>
                    <span v-if="!touched" class="tag tag--default">réglages par défaut</span>
                </div>
            </div>
            <div class="row-qty">
                <!-- Avec plusieurs unités : champ composé (valeur | unité abrégée, libellés complets dans la liste). Sinon champ simple + unité. -->
                <div class="qty-group" :class="{'qty-group--unit': hasUnitChoice}">
                    <div class="qty-wrap">
                        <el-input class="qty-in" :value="ingredient.pivot_quantity" type="number" min="0" step="any"
                                  :aria-label="'Quantité de ' + (ingredient.pivot_label || ingredient.label)"
                                  :id="'qty-' + ingredient.uniqid"
                                  @input="emit('pivot_quantity', $event)" @change="$emit('quantity-change')"></el-input>
                        <!-- Flèches maison (les natives ne se stylent pas) : ± 1, recalcul comme un changement de quantité -->
                        <span class="qty-arrows" aria-hidden="true">
                            <button type="button" tabindex="-1" @mousedown.prevent @click="stepQty(1)"><svg width="9" height="9" viewBox="0 0 12 12"><path d="M2 8l4-4 4 4" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg></button>
                            <button type="button" tabindex="-1" @mousedown.prevent @click="stepQty(-1)"><svg width="9" height="9" viewBox="0 0 12 12"><path d="M2 4l4 4 4-4" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg></button>
                        </span>
                    </div>
                    <el-select popper-class="calc-pop" v-if="hasUnitChoice" class="unit-sel" :value="ingredient.pivot_quantity_unit"
                               :title="unitLabel" :aria-label="'Unité de ' + ingredient.label"
                               @change="emit('pivot_quantity_unit', $event); $emit('quantity-change')">
                        <el-option v-for="u in unitOptions" :key="u.value" :label="abbr(u.label)" :value="u.value">{{ u.label }}</el-option>
                    </el-select>
                    <span v-else class="unit" :title="unitLabel">{{ abbr(unitLabel) }}</span>
                </div>
            </div>
            <div class="row-contrib" v-if="ingredient.pivot_quantity && isDef(ingredient.result)">
                <div class="n"><span class="tnum">{{ fmt(ingredient.result) }} g/couv.</span><span class="tnum">{{ fmt(percent) }} %</span></div>
                <div class="bar"><div class="bar-fill" :class="{'bar-fill--accent': dominant}" :style="{width: percent + '%'}"></div></div>
            </div>
            <div class="row-contrib hint" v-else>Saisissez une quantité</div>
            <div class="row-actions">
                <button type="button" :aria-expanded="expanded ? 'true' : 'false'" :aria-controls="rowId + '-more'"
                        :aria-label="(expanded ? 'Masquer' : 'Afficher') + ' les réglages de ' + ingredient.label" @click="$emit('toggle')">
                    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true"><path d="M4 7h10M18 7h2M4 17h4M12 17h8"/><circle cx="16" cy="7" r="2"/><circle cx="10" cy="17" r="2"/></svg>
                </button>
                <button type="button" class="danger" :aria-label="'Supprimer ' + ingredient.label" @click="$emit('remove')">
                    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true"><path d="M4 7h16M9 7V5h6v2M7 7l1 12h8l1-12"/></svg>
                </button>
            </div>
        </div>
        <p v-if="error" class="field-error">{{ error }}</p>
        <div class="row-more" :id="rowId + '-more'" :hidden="!expanded">
            <div class="form-grid">
                <div class="full">
                    <label class="lbl" :for="rowId + '-label'">Nom personnalisé</label>
                    <el-input :id="rowId + '-label'" :value="ingredient.pivot_label" :placeholder="ingredient.label" @input="emit('pivot_label', $event)"></el-input>
                </div>
                <div>
                    <label class="lbl">Origine</label>
                    <el-select popper-class="calc-pop" :value="ingredient.pivot_provenance" :aria-label="'Origine de ' + ingredient.label" @change="emit('pivot_provenance', $event, true)">
                        <el-option v-for="o in type.fields.provenance.options" :key="o.value" :label="o.label" :value="o.value"></el-option>
                    </el-select>
                </div>
                <div>
                    <label class="lbl">Conservation</label>
                    <el-select popper-class="calc-pop" :value="ingredient.pivot_mode_conserv" :aria-label="'Conservation de ' + ingredient.label" @change="emit('pivot_mode_conserv', $event, true)">
                        <el-option v-for="o in type.fields.mode_conserv.options" :key="o.value" :label="o.label" :value="o.value"></el-option>
                    </el-select>
                </div>
                <div>
                    <label class="lbl">Cuisson</label>
                    <el-select popper-class="calc-pop" :value="ingredient.pivot_cuisson" :aria-label="'Cuisson de ' + ingredient.label" @change="emit('pivot_cuisson', $event, true); $emit('quantity-change')">
                        <el-option v-for="o in (ingredient.options_pivot_cuisson || type.fields.cuisson.options)" :key="o.value" :label="o.label" :value="o.value"></el-option>
                    </el-select>
                </div>
                <div>
                    <label class="lbl">Label</label>
                    <el-select popper-class="calc-pop" :value="ingredient.pivot_type_label" clearable :aria-label="'Label de ' + ingredient.label" @change="emit('pivot_type_label', $event, true); $emit('quantity-change')">
                        <el-option v-for="o in type.fields.label_product.options" :key="o.value" :label="o.label" :value="o.value"></el-option>
                    </el-select>
                </div>
                <div v-if="hasSaison">
                    <label class="lbl">Saisonnalité</label>
                    <el-select popper-class="calc-pop" :value="ingredient.pivot_saison" :aria-label="'Saisonnalité de ' + ingredient.label" @change="emit('pivot_saison', $event, true)">
                        <el-option v-for="o in type.fields.saison.options" :key="o.value" :label="o.label" :value="o.value"></el-option>
                    </el-select>
                </div>
                <div v-if="weights" class="ro">
                    <span class="lbl">Poids</span>
                    <span class="tnum">brut {{ fmt(weights.poids_raw) }} g · comestible {{ fmt(weights.poids_comestible) }} g · après cuisson {{ fmt(weights.poids) }} g</span>
                </div>
            </div>
            <p v-if="rendementLabel" class="hint">Quantité saisie en valeur {{ rendementLabel }}.</p>
        </div>
    </div>
</template>

<script>
import format from './format';

export default {
    name: 'IngredientRow',
    mixins: [format],
    props: {
        ingredient: {type: Object, required: true},
        type: {type: Object, required: true},
        expanded: {type: Boolean, default: false},
        touched: {type: Boolean, default: true},
        dominant: {type: Boolean, default: false},
        totalResult: {type: Number, default: 0},
        weights: {type: Object, default: null},
        rendementLabel: {type: String, default: null},
        error: {type: String, default: null},
        rowId: {type: String, required: true},
    },
    computed: {
        percent() {
            if (!this.totalResult) return 0;
            return this.round((parseFloat(this.ingredient.result) || 0) / this.totalResult * 100);
        },
        // Le select de saison n'existe que pour fruit-legume-herbes (les autres types ont un champ « inline » sans options)
        hasSaison() {
            return this.type.slug === 'fruit-legume-herbes' && this.type.fields.saison && Array.isArray(this.type.fields.saison.options);
        },
        isImported() {
            return String(this.ingredient.pivot_provenance || '').indexOf('reste_du_monde') === 0
                || ['amerique_du_nord', 'amerique_du_sud', 'asie', 'afrique'].indexOf(this.ingredient.pivot_provenance) !== -1;
        },
        unitOptions() {
            const o = this.ingredient.options_pivot_quantity_unit;
            return Array.isArray(o) && o.length ? o : [{label: 'Gramme(s)', value: 'grammes'}];
        },
        hasUnitChoice() {
            return this.unitOptions.length > 1;
        },
        // Libellé complet de l'unité courante (les options portent {label, value:slug})
        unitLabel() {
            const slug = this.ingredient.pivot_quantity_unit || 'grammes';
            const o = this.unitOptions.find((x) => x.value === slug);
            return o ? o.label : (slug === 'grammes' ? 'grammes' : slug);
        },
    },
    methods: {
        // Abréviation affichée dans le champ ; le libellé complet reste dans la liste et dans le title
        abbr(label) {
            const l = String(label || '').trim();
            const map = {
                'grammes': 'g', 'gramme(s)': 'g', 'unité (s)': 'u.', 'kg': 'kg', 'cl': 'cl', 'l': 'l', 'dl': 'dl', 'ml': 'ml',
                'demi litre': '½ l', 'cuillère(s) à soupe': 'c. à s.', 'cuillère(s) à café': 'c. à c.', 'demi cuillère à café': '½ c. à c.',
                'cuillère': 'cuil.', 'pincée(s)': 'pincée', 'gousse(s)': 'gousse', 'tranche(s)': 'tr.', 'feuille(s)': 'feuille',
                'pièce(s)': 'pc', 'portion(s)': 'port.', 'poignée(s)': 'poign.', 'bouquet(s)': 'bouq.', 'morceau(x)': 'mcx',
                'verre(s) de 12 cl': 'v. 12 cl', 'verre(s) de 20 cl': 'v. 20 cl', 'verre(s) de 3 cl': 'v. 3 cl', 'verre(s) de 25 cl': 'v. 25 cl',
                'centimètre': 'cm', 'douzaine': 'dz', 'livre': 'lb',
            };
            const k = l.toLowerCase();
            if (map[k]) return map[k];
            // Repli : sans les marques de pluriel, 8 caractères maximum
            const base = l.replace(/\((s|x)\)/g, '').trim();
            return base.length > 8 ? base.slice(0, 7) + '.' : base;
        },
        optionLabel(field, value) {
            const f = this.type.fields[field];
            const o = f && Array.isArray(f.options) ? f.options.find((x) => x.value === value) : null;
            return o ? o.label : value;
        },
        stepQty(delta) {
            const current = parseFloat(String(this.ingredient.pivot_quantity || 0).replace(',', '.')) || 0;
            const next = Math.max(0, Math.round((current + delta) * 100) / 100);
            this.emit('pivot_quantity', next);
            this.$emit('quantity-change');
        },
        emit(key, value, isSetting = false) {
            this.$emit('update', {key, value, isSetting});
        },
    },
};
</script>
